import { useEffect } from 'react';
import { applyThemeVars } from '@aquilixshop/ui-react';
import { useBuilderStore } from '../state/builderStore';

/**
 * Projects the store's `--aqx-theme-*` palette onto the DOCUMENT ROOT, for the builder CHROME.
 *
 * `BuilderCanvas` already projects these onto its `[data-aqx-page]` wrapper — but that wrapper only
 * covers the PREVIEW. Every chrome surface that paints a theme colour lives OUTSIDE it: the inspector
 * ColorControl swatch, its theme-token grid, the section-rail previews. Those set
 * `background: var(--aqx-theme-accent-2)` on an element where the var was never defined, so the swatch
 * painted TRANSPARENT — a theme-bound colour looked like "no colour" while the canvas showed it fine.
 *
 * Only the `--aqx-theme-*` names are projected (never the legacy `--aqx-purple`/`--aqx-line` chrome
 * aliases `applyThemeVars` also emits) so the Polaris chrome palette is untouched. The canvas keeps its
 * own inline copy, which wins over this root declaration — this is a FALLBACK for chrome, not a
 * replacement.
 */
export function useChromeThemeVars(): void {
  const themeSettings = useBuilderStore((s) => s.themeSettings);

  useEffect(() => {
    const root = document.documentElement;
    const vars = applyThemeVars(themeSettings) as Record<string, string>;
    const applied: string[] = [];
    for (const [name, value] of Object.entries(vars)) {
      if (!name.startsWith('--aqx-theme-')) continue;
      root.style.setProperty(name, value);
      applied.push(name);
    }
    return () => {
      for (const name of applied) root.style.removeProperty(name);
    };
  }, [themeSettings]);
}
